import { VideoView, useVideoPlayer } from 'expo-video';
import type { VideoPlayerStatus } from 'expo-video';
import { useEventListener } from 'expo';
import { Image as ExpoImage } from 'expo-image';
import { router } from 'expo-router';
import {
  Minimize2,
  MessageCircle,
  Pause,
  Play,
  Share2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react-native';
import {
  createElement,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  FlatList,
  type GestureResponderEvent,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/src/components/Button';
import { IdentityHeader } from '@/src/components/IdentityHeader';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import { useQueuedPostReaction } from '@/src/features/offline/use-outbox';
import {
  useFollowState,
  useToggleFollow,
} from '@/src/features/profile/use-profile';
import {
  nativeShareDescription,
  nativeShareTitle,
  ShareOptionsModal,
} from '@/src/features/sharing/ShareOptionsModal';
import {
  openFacebookShareDialog,
  shareLinkWithDevice,
} from '@/src/features/sharing/share-link';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { copyTextToClipboard } from '@/src/utils/clipboard';
import { formatDate } from '@/src/utils/dates';
import { openUserProfile } from '@/src/utils/profile-navigation';

import {
  applyReactionTransition,
  CompactReactionButton,
  createReactionMutationId,
  ReactionDetailsModal,
  reactionMeta,
  shouldClearOptimisticReaction,
} from './ReactionControls';
import {
  ProfileMentionInput,
  visibleProfileMentions,
} from './ProfileMentionInput';
import { ProfileMentionText } from './ProfileMentionText';
import {
  createPost,
  emptyReactionCounts,
  MAX_POST_BODY_LENGTH,
  recordPostShare,
  type ProfileMention,
  type PublicPost,
  type Reaction,
  type ReactionCounts,
} from './post-service';
import { getTopUniqueReactions } from './reaction-summary';
import { usePostReaction, useSetPostReaction } from './use-post-like';
import {
  mergeVideoViewerQueue,
  videoPostIndexForPostId,
  videoPostsFromPosts,
  videoViewerIndexForScrollOffset,
} from './video-viewer-queue';

type OptimisticReactionState = {
  baseCounts: ReactionCounts;
  baseReaction: Reaction | null;
  mutationId: string;
  reaction: Reaction | null;
};

type SharePostAction = 'copy' | 'facebook' | 'native' | 'reshare';
type SharePostMethod = 'clipboard' | 'facebook' | 'native' | 'reshare' | 'web';
type VideoProgressState = {
  bufferedPosition: number;
  currentTime: number;
  duration: number;
};

const EMPTY_VIDEO_PROGRESS: VideoProgressState = {
  bufferedPosition: 0,
  currentTime: 0,
  duration: 0,
};

function WebVideoSlide({
  active,
  onProgressChange,
  onRegisterSeek,
  height,
  muted,
  onTogglePlayback,
  post,
  width,
}: {
  active: boolean;
  height: number;
  muted: boolean;
  onProgressChange: (postId: string, progress: VideoProgressState) => void;
  onRegisterSeek: (
    postId: string,
    seeker: ((seconds: number) => void) | null,
  ) => void;
  onTogglePlayback: (playing: boolean) => void;
  post: PublicPost;
  width: number;
}) {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [slowLoad, setSlowLoad] = useState(false);
  const media = post.media;
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const updateProgress = useCallback(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    const safeDuration = Number.isFinite(video.duration) ? video.duration : 0;
    const currentTime = Number.isFinite(video.currentTime)
      ? video.currentTime
      : 0;
    const bufferedPosition =
      video.buffered.length > 0
        ? video.buffered.end(video.buffered.length - 1)
        : currentTime;

    setDuration(safeDuration);
    onProgressChange(post.id, {
      bufferedPosition,
      currentTime,
      duration: safeDuration,
    });
  }, [onProgressChange, post.id]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    video.loop = true;
    video.muted = muted;
    video.volume = 1;
  }, [muted]);

  useEffect(() => {
    onRegisterSeek(post.id, (seconds: number) => {
      const video = videoRef.current;

      if (!video) {
        return;
      }

      const safeDuration = Number.isFinite(video.duration)
        ? video.duration
        : duration;
      const nextTime = Math.max(
        0,
        Math.min(seconds, safeDuration > 0 ? safeDuration : seconds),
      );

      video.currentTime = nextTime;
      updateProgress();
    });

    return () => onRegisterSeek(post.id, null);
  }, [duration, onRegisterSeek, post.id, updateProgress]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (!active) {
      video.pause();
      setPlaying(false);
      setSlowLoad(false);
      return;
    }

    const playResult = video.play();
    setPlaying(true);

    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => {
        setPlaying(false);
        onTogglePlayback(false);
      });
    }
  }, [active, media?.downloadURL, onTogglePlayback]);

  useEffect(() => {
    if (!active || ready) {
      setSlowLoad(false);
      return undefined;
    }

    const timeout = setTimeout(() => setSlowLoad(true), 1800);

    return () => clearTimeout(timeout);
  }, [active, ready]);

  function togglePlayback() {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (playing) {
      video.pause();
      setPlaying(false);
      onTogglePlayback(false);
      return;
    }

    const playResult = video.play();
    setPlaying(true);
    onTogglePlayback(true);

    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => {
        setPlaying(false);
        onTogglePlayback(false);
      });
    }
  }

  const inlineVideo = createElement('video', {
    'aria-label': 'Post video',
    controls: false,
    controlsList: 'nodownload noplaybackrate noremoteplayback',
    disablePictureInPicture: true,
    disableRemotePlayback: true,
    loop: true,
    muted,
    onCanPlay: () => setReady(true),
    onLoadedMetadata: () => {
      setReady(true);
      updateProgress();
    },
    onPause: () => setPlaying(false),
    onPlay: () => setPlaying(true),
    onProgress: updateProgress,
    onTimeUpdate: updateProgress,
    onWaiting: () => setReady(false),
    playsInline: true,
    preload: 'auto',
    ref: videoRef,
    src: media?.downloadURL,
    style: {
      backgroundColor: '#000000',
      height: '100%',
      objectFit: 'contain',
      pointerEvents: 'none',
      width: '100%',
    },
    'webkit-playsinline': 'true',
    webkitPlaysInline: 'true',
    'x5-playsinline': 'true',
    x5PlaysInline: 'true',
  } as Record<string, unknown>);

  return (
    <Pressable
      accessibilityLabel={playing ? 'Pause video' : 'Play video'}
      accessibilityRole="button"
      onPress={togglePlayback}
      style={[styles.slide, { height, width }]}
    >
      <View style={styles.video}>{inlineVideo}</View>
      {active && !ready ? (
        <View pointerEvents="none" style={styles.videoLoadingOverlay}>
          <ActivityIndicator color={colors.surface} />
          <Text style={styles.videoLoadingText}>
            {slowLoad ? 'Still loading video...' : 'Loading video...'}
          </Text>
        </View>
      ) : null}
      {!playing ? (
        <View pointerEvents="none" style={styles.centerPlayIndicator}>
          <Play color={colors.surface} fill={colors.surface} size={38} />
        </View>
      ) : null}
    </Pressable>
  );
}

function NativeVideoSlide({
  active,
  onProgressChange,
  onRegisterSeek,
  height,
  muted,
  onTogglePlayback,
  post,
  width,
}: {
  active: boolean;
  height: number;
  muted: boolean;
  onProgressChange: (postId: string, progress: VideoProgressState) => void;
  onRegisterSeek: (
    postId: string,
    seeker: ((seconds: number) => void) | null,
  ) => void;
  onTogglePlayback: (playing: boolean) => void;
  post: PublicPost;
  width: number;
}) {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [status, setStatus] = useState<VideoPlayerStatus>('idle');
  const [slowLoad, setSlowLoad] = useState(false);
  const media = post.media;
  const player = useVideoPlayer(media?.downloadURL ?? '', (videoPlayer) => {
    videoPlayer.loop = true;
    videoPlayer.muted = muted;
    videoPlayer.timeUpdateEventInterval = 0.25;
    videoPlayer.volume = 1;
  });

  useEffect(() => {
    player.loop = true;
    player.muted = muted;
    player.timeUpdateEventInterval = 0.25;
  }, [muted, player]);

  useEventListener(player, 'statusChange', ({ status: nextStatus }) => {
    setStatus(nextStatus);
  });

  useEventListener(player, 'sourceLoad', ({ duration: nextDuration }) => {
    const safeDuration = Number.isFinite(nextDuration) ? nextDuration : 0;

    setDuration(safeDuration);
    onProgressChange(post.id, {
      bufferedPosition: 0,
      currentTime: 0,
      duration: safeDuration,
    });
  });

  useEventListener(player, 'timeUpdate', (payload) => {
    const safeDuration = Number.isFinite(player.duration)
      ? player.duration
      : duration;
    const currentTime = Number.isFinite(payload.currentTime)
      ? payload.currentTime
      : 0;

    onProgressChange(post.id, {
      bufferedPosition: Number.isFinite(payload.bufferedPosition)
        ? payload.bufferedPosition
        : 0,
      currentTime,
      duration: safeDuration,
    });

    if (safeDuration !== duration) {
      setDuration(safeDuration);
    }
  });

  useEffect(() => {
    onRegisterSeek(post.id, (seconds: number) => {
      const safeDuration = Number.isFinite(player.duration)
        ? player.duration
        : duration;
      const nextTime = Math.max(
        0,
        Math.min(seconds, safeDuration > 0 ? safeDuration : seconds),
      );

      player.currentTime = nextTime;
      onProgressChange(post.id, {
        bufferedPosition: 0,
        currentTime: nextTime,
        duration: safeDuration,
      });
    });

    return () => onRegisterSeek(post.id, null);
  }, [duration, onProgressChange, onRegisterSeek, player, post.id]);

  useEffect(() => {
    if (!active) {
      player.pause();
      setPlaying(false);
      setSlowLoad(false);
      return;
    }

    player.play();
    setPlaying(true);
  }, [active, media, player]);

  useEffect(() => {
    if (!active || status === 'readyToPlay') {
      setSlowLoad(false);
      return undefined;
    }

    const timeout = setTimeout(() => setSlowLoad(true), 1800);

    return () => clearTimeout(timeout);
  }, [active, status]);

  function togglePlayback() {
    if (playing) {
      player.pause();
      setPlaying(false);
      onTogglePlayback(false);
      return;
    }

    player.play();
    setPlaying(true);
    onTogglePlayback(true);
  }

  return (
    <Pressable
      accessibilityLabel={playing ? 'Pause video' : 'Play video'}
      accessibilityRole="button"
      onPress={togglePlayback}
      style={[styles.slide, { height, width }]}
    >
      <VideoView
        allowsFullscreen={false}
        contentFit="contain"
        nativeControls={false}
        player={player}
        style={styles.video}
      />
      {active && status !== 'readyToPlay' ? (
        <View pointerEvents="none" style={styles.videoLoadingOverlay}>
          <ActivityIndicator color={colors.surface} />
          <Text style={styles.videoLoadingText}>
            {slowLoad ? 'Still loading video...' : 'Loading video...'}
          </Text>
        </View>
      ) : null}
      {!playing ? (
        <View pointerEvents="none" style={styles.centerPlayIndicator}>
          <Play color={colors.surface} fill={colors.surface} size={38} />
        </View>
      ) : null}
    </Pressable>
  );
}

function VideoSlide(props: {
  active: boolean;
  height: number;
  muted: boolean;
  onProgressChange: (postId: string, progress: VideoProgressState) => void;
  onRegisterSeek: (
    postId: string,
    seeker: ((seconds: number) => void) | null,
  ) => void;
  onTogglePlayback: (playing: boolean) => void;
  post: PublicPost;
  width: number;
}) {
  if (Platform.OS === 'web') {
    return <WebVideoSlide {...props} />;
  }

  return <NativeVideoSlide {...props} />;
}

function ReshareVideoComposeModal({
  body,
  currentUid,
  mentions,
  onBodyChange,
  onClose,
  onMentionsChange,
  onSubmit,
  post,
  posting,
  visible,
}: {
  body: string;
  currentUid?: string;
  mentions: ProfileMention[];
  onBodyChange: (body: string) => void;
  onClose: () => void;
  onMentionsChange: (mentions: ProfileMention[]) => void;
  onSubmit: () => void;
  post: PublicPost;
  posting: boolean;
  visible: boolean;
}) {
  const author = post.authorFullName ?? post.authorUsername ?? 'Frienzzi user';

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <Pressable
        disabled={posting}
        onPress={onClose}
        style={styles.composeBackdrop}
      >
        <Pressable
          onPress={(event) => event.stopPropagation()}
          style={styles.composeCard}
        >
          <View style={styles.composeHeader}>
            <View style={styles.composeTitleBlock}>
              <Text style={styles.composeTitle}>Reshare on Frienzzi</Text>
              <Text style={styles.composeSubtitle}>
                Add your own message or reshare this video as-is.
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Close reshare composer"
              accessibilityRole="button"
              disabled={posting}
              onPress={onClose}
              style={({ pressed }) => [
                styles.composeCloseButton,
                pressed && !posting ? styles.overlayButtonPressed : null,
              ]}
            >
              <X color={colors.mutedText} size={20} strokeWidth={2.5} />
            </Pressable>
          </View>
          <ScrollView
            contentContainerStyle={styles.composeScrollContent}
            keyboardShouldPersistTaps="handled"
            style={styles.composeScroll}
          >
            <ProfileMentionInput
              label="Your message"
              autoCapitalize="sentences"
              maxLength={MAX_POST_BODY_LENGTH}
              mentions={mentions}
              multiline
              numberOfLines={4}
              onChangeText={onBodyChange}
              onMentionsChange={onMentionsChange}
              placeholder="Add a thought..."
              scope={{ kind: 'post' }}
              style={styles.composeInput}
              textAlignVertical="top"
              value={body}
            />
            <Text style={styles.composeCounter}>
              {body.length}/{MAX_POST_BODY_LENGTH}
            </Text>
            <View style={styles.resharePreview}>
              <Text style={styles.resharePreviewEyebrow}>Video post</Text>
              <Text numberOfLines={1} style={styles.resharePreviewTitle}>
                {author}
              </Text>
              {post.body ? (
                <ProfileMentionText
                  body={post.body}
                  currentUid={currentUid}
                  mentions={post.profileMentions ?? []}
                  source="post_reshare_preview"
                  style={styles.resharePreviewBody}
                />
              ) : (
                <Text style={styles.resharePreviewBody}>
                  This reshare will include the original video.
                </Text>
              )}
            </View>
          </ScrollView>
          <View style={styles.composeActions}>
            <Button
              title="Cancel"
              variant="secondary"
              disabled={posting}
              style={styles.composeButton}
              onPress={onClose}
            />
            <Button
              title="Reshare"
              loading={posting}
              loadingTitle="Resharing..."
              style={styles.composeButton}
              onPress={onSubmit}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function VideoActions({
  currentUid,
  onActorProfilePress,
  onCommentPress,
  onOpenShare,
  onReaction,
  post,
  reactionCounts,
  reactionDisabled,
  reactionLoading,
  selectedReaction,
  shareCount,
  totalReactions,
}: {
  currentUid?: string;
  onActorProfilePress?: (targetUid: string) => void;
  onCommentPress: () => void;
  onOpenShare: () => void;
  onReaction: (reaction: Reaction) => void;
  post: PublicPost;
  reactionCounts: ReactionCounts;
  reactionDisabled: boolean;
  reactionLoading: boolean;
  selectedReaction: Reaction | null;
  shareCount: number;
  totalReactions: number;
}) {
  const [reactionDetailsVisible, setReactionDetailsVisible] = useState(false);
  const topReactionIcons = getTopUniqueReactions(reactionCounts);

  return (
    <View style={styles.actionsRail}>
      <View style={styles.actionCluster}>
        <View style={styles.railIconCircle}>
          <CompactReactionButton
            disabled={reactionDisabled}
            loading={reactionLoading}
            pickerOpensLeft
            selectedReaction={selectedReaction}
            onSelect={onReaction}
          />
        </View>
        <Text style={styles.actionCount}>{totalReactions}</Text>
      </View>
      <Pressable
        accessibilityLabel="View comments"
        accessibilityRole="button"
        onPress={onCommentPress}
        style={({ pressed }) => [
          styles.railButton,
          pressed ? styles.railButtonPressed : null,
        ]}
      >
        <View style={styles.railIconCircle}>
          <MessageCircle color={colors.surface} size={25} strokeWidth={2.4} />
        </View>
        <Text style={styles.actionCount}>{post.commentCount}</Text>
      </Pressable>
      {post.visibility === 'public' ? (
        <Pressable
          accessibilityLabel="Share post"
          accessibilityRole="button"
          onPress={onOpenShare}
          style={({ pressed }) => [
            styles.railButton,
            pressed ? styles.railButtonPressed : null,
          ]}
        >
          <View style={styles.railIconCircle}>
            <Share2 color={colors.surface} size={24} strokeWidth={2.4} />
          </View>
          <Text style={styles.actionCount}>{shareCount}</Text>
        </Pressable>
      ) : null}
      {topReactionIcons.length > 0 ? (
        <>
          <Pressable
            accessibilityLabel="View reactions"
            accessibilityRole="button"
            onPress={() => {
              if (!currentUid) {
                router.push('/sign-in');
                return;
              }

              setReactionDetailsVisible(true);
            }}
            style={({ pressed }) => [
              styles.topReactionButton,
              pressed ? styles.railButtonPressed : null,
            ]}
          >
            <View style={styles.topReactionStack}>
              {topReactionIcons.map((reaction, index) => (
                <View
                  key={`${reaction}-${index}`}
                  style={[
                    styles.topReactionBubble,
                    index > 0 ? styles.topReactionBubbleOverlap : null,
                  ]}
                >
                  <Text style={styles.topReactionEmoji}>
                    {reactionMeta[reaction].emoji}
                  </Text>
                </View>
              ))}
            </View>
          </Pressable>
          {currentUid ? (
            <ReactionDetailsModal
              counts={reactionCounts}
              currentUid={currentUid}
              target={{ kind: 'post', postId: post.id }}
              visible={reactionDetailsVisible}
              onActorProfilePress={onActorProfilePress}
              onClose={() => setReactionDetailsVisible(false)}
            />
          ) : null}
        </>
      ) : null}
    </View>
  );
}

function formatPlaybackTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '0:00';
  }

  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(
      remainingSeconds,
    ).padStart(2, '0')}`;
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
}

function VideoProgressControl({
  onSeek,
  onTogglePlayback,
  playing,
  progress,
}: {
  onSeek: (seconds: number) => void;
  onTogglePlayback: () => void;
  playing: boolean;
  progress: VideoProgressState;
}) {
  const [trackWidth, setTrackWidth] = useState(0);
  const duration = Number.isFinite(progress.duration) ? progress.duration : 0;
  const currentTime = Math.max(
    0,
    Math.min(
      Number.isFinite(progress.currentTime) ? progress.currentTime : 0,
      duration > 0 ? duration : progress.currentTime,
    ),
  );
  const bufferedPosition = Math.max(
    0,
    Math.min(
      Number.isFinite(progress.bufferedPosition)
        ? progress.bufferedPosition
        : 0,
      duration > 0 ? duration : progress.bufferedPosition,
    ),
  );
  const progressRatio = duration > 0 ? currentTime / duration : 0;
  const bufferedRatio = duration > 0 ? bufferedPosition / duration : 0;

  function seekFromGesture(event: GestureResponderEvent) {
    event.stopPropagation();

    if (trackWidth <= 0 || duration <= 0) {
      return;
    }

    const locationX = Math.max(
      0,
      Math.min(event.nativeEvent.locationX, trackWidth),
    );

    onSeek((locationX / trackWidth) * duration);
  }

  return (
    <View style={styles.progressControl}>
      <Pressable
        accessibilityLabel={playing ? 'Pause video' : 'Play video'}
        accessibilityRole="button"
        onPress={(event) => {
          event.stopPropagation();
          onTogglePlayback();
        }}
        style={({ pressed }) => [
          styles.progressPlayButton,
          pressed ? styles.progressPlayButtonPressed : null,
        ]}
      >
        {playing ? (
          <Pause color={colors.surface} size={16} strokeWidth={2.7} />
        ) : (
          <Play color={colors.surface} fill={colors.surface} size={16} />
        )}
      </Pressable>
      <View style={styles.progressMain}>
        <View
          accessible
          accessibilityLabel={`Video progress ${formatPlaybackTime(
            currentTime,
          )} of ${formatPlaybackTime(duration)}`}
          accessibilityRole="adjustable"
          onLayout={(event) => setTrackWidth(event.nativeEvent.layout.width)}
          onMoveShouldSetResponder={() => true}
          onResponderGrant={seekFromGesture}
          onResponderMove={seekFromGesture}
          onStartShouldSetResponder={() => true}
          style={styles.progressTrack}
        >
          <View
            pointerEvents="none"
            style={[
              styles.progressBuffered,
              { width: `${Math.min(bufferedRatio * 100, 100)}%` },
            ]}
          />
          <View
            pointerEvents="none"
            style={[
              styles.progressFill,
              { width: `${Math.min(progressRatio * 100, 100)}%` },
            ]}
          />
          <View
            pointerEvents="none"
            style={[
              styles.progressThumb,
              { left: `${Math.min(progressRatio * 100, 100)}%` },
            ]}
          />
        </View>
        <View style={styles.progressTimeRow}>
          <Text style={styles.progressTimeText}>
            {formatPlaybackTime(currentTime)}
          </Text>
          <Text style={styles.progressTimeText}>
            {formatPlaybackTime(duration)}
          </Text>
        </View>
      </View>
    </View>
  );
}

function VideoOverlay({
  captionExpanded,
  currentUid,
  muted,
  onSeek,
  onClose,
  onMinimize,
  onOpenAuthor,
  onTogglePlayback,
  onToggleCaption,
  onToggleMuted,
  post,
  playing,
  progress,
}: {
  captionExpanded: boolean;
  currentUid?: string;
  muted: boolean;
  onSeek: (seconds: number) => void;
  onClose: () => void;
  onMinimize: () => void;
  onOpenAuthor: () => void;
  onTogglePlayback: () => void;
  onToggleCaption: () => void;
  onToggleMuted: () => void;
  playing: boolean;
  post: PublicPost;
  progress: VideoProgressState;
}) {
  const { showError, showSuccess } = useToast();
  const followState = useFollowState(currentUid, post.authorUid);
  const toggleFollow = useToggleFollow(currentUid, post.authorUid);
  const authorFullName = post.authorFullName || 'Frienzzi user';
  const authorUsername = post.authorUsername;
  const authorPhotoURL = post.authorPhotoURL;
  const authorVerifiedBadge = post.authorVerifiedBadge === true;
  const authorFounderBadge = post.authorFounderBadge === true;
  const hasLongCaption = Boolean(post.body && post.body.length > 150);
  const canFollow = Boolean(currentUid && currentUid !== post.authorUid);
  const followActionPending = followState.loading || toggleFollow.isPending;

  function toggleAuthorFollow() {
    if (!currentUid) {
      router.push('/sign-in');
      return;
    }

    if (!canFollow || followActionPending) {
      return;
    }

    const nextFollowing = !followState.following;
    trackEvent('follow_toggle', {
      following: nextFollowing,
      result: 'started',
      source: 'post_video_viewer',
    });
    toggleFollow.mutate(
      { following: nextFollowing },
      {
        onError: (error) => {
          showError(error, 'Unable to update follow.');
        },
        onSuccess: (result) => {
          if (result === 'queued') {
            showSuccess('Follow update queued.');
            return;
          }

          showSuccess(
            nextFollowing
              ? `You are now following ${authorFullName}.`
              : `You unfollowed ${authorFullName}.`,
            nextFollowing ? 'Following' : 'Unfollowed',
          );
        },
      },
    );
  }

  return (
    <>
      <View pointerEvents="box-none" style={styles.topOverlay}>
        <View style={styles.topLeftActions}>
          <Pressable
            accessibilityLabel="Close video viewer"
            accessibilityRole="button"
            onPress={onClose}
            style={({ pressed }) => [
              styles.overlayButton,
              pressed ? styles.overlayButtonPressedDark : null,
            ]}
          >
            <X color={colors.surface} size={24} strokeWidth={2.5} />
          </Pressable>
          <Pressable
            accessibilityLabel="Minimize video"
            accessibilityRole="button"
            onPress={onMinimize}
            style={({ pressed }) => [
              styles.overlayButton,
              pressed ? styles.overlayButtonPressedDark : null,
            ]}
          >
            <Minimize2 color={colors.surface} size={21} strokeWidth={2.5} />
          </Pressable>
        </View>
        <Pressable
          accessibilityLabel={muted ? 'Turn sound on' : 'Mute video'}
          accessibilityRole="button"
          onPress={onToggleMuted}
          style={({ pressed }) => [
            styles.overlayButton,
            pressed ? styles.overlayButtonPressedDark : null,
          ]}
        >
          {muted ? (
            <VolumeX color={colors.surface} size={22} strokeWidth={2.5} />
          ) : (
            <Volume2 color={colors.surface} size={22} strokeWidth={2.5} />
          )}
        </Pressable>
      </View>
      <View pointerEvents="box-none" style={styles.bottomContent}>
        <View style={styles.bottomOverlay}>
          <View style={styles.authorRow}>
            <IdentityHeader
              avatarSize={44}
              containerStyle={styles.viewerIdentity}
              currentUid={currentUid}
              density="viewer"
              founderBadge={authorFounderBadge}
              fullName={authorFullName}
              metadata={[
                {
                  id: 'created',
                  kind: 'text',
                  label: formatDate(post.createdAt),
                },
              ]}
              photoURL={authorPhotoURL}
              uid={post.authorUid}
              username={authorUsername}
              variant="overlay"
              verifiedBadge={authorVerifiedBadge}
              onPress={onOpenAuthor}
            />
            {canFollow ? (
              <Pressable
                accessibilityLabel={
                  followState.following
                    ? `Unfollow ${authorFullName}`
                    : `Follow ${authorFullName}`
                }
                accessibilityRole="button"
                disabled={followActionPending}
                onPress={toggleAuthorFollow}
                style={({ pressed }) => [
                  styles.followButton,
                  followState.following ? styles.followButtonFollowing : null,
                  pressed && !followActionPending
                    ? styles.followButtonPressed
                    : null,
                  followActionPending ? styles.followButtonDisabled : null,
                ]}
              >
                {followActionPending ? (
                  <ActivityIndicator color={colors.surface} size="small" />
                ) : (
                  <Text
                    style={[
                      styles.followButtonText,
                      followState.following
                        ? styles.followButtonTextFollowing
                        : null,
                    ]}
                  >
                    {followState.following ? 'Following' : 'Follow'}
                  </Text>
                )}
              </Pressable>
            ) : null}
          </View>
          {post.body ? (
            <Pressable
              accessibilityRole="button"
              disabled={!hasLongCaption}
              onPress={onToggleCaption}
              style={styles.captionPressable}
            >
              <ScrollView
                scrollEnabled={captionExpanded}
                showsVerticalScrollIndicator={captionExpanded}
                style={[
                  styles.captionScroll,
                  captionExpanded ? styles.captionScrollExpanded : null,
                ]}
              >
                <ProfileMentionText
                  body={post.body}
                  currentUid={currentUid}
                  mentionStyle={styles.viewerCaptionMention}
                  mentions={post.profileMentions ?? []}
                  numberOfLines={captionExpanded ? undefined : 2}
                  source="video_viewer_caption"
                  style={styles.viewerCaption}
                />
              </ScrollView>
              {hasLongCaption ? (
                <Text style={styles.moreText}>
                  {captionExpanded ? 'Show less' : 'Show more'}
                </Text>
              ) : null}
            </Pressable>
          ) : null}
        </View>
        <VideoProgressControl
          playing={playing}
          progress={progress}
          onSeek={onSeek}
          onTogglePlayback={onTogglePlayback}
        />
      </View>
    </>
  );
}

function ActiveVideoControls({
  onActorProfilePress,
  onCommentPress,
  onPostEngagement,
  post,
}: {
  onActorProfilePress?: (targetUid: string) => void;
  onCommentPress: (post: PublicPost) => void;
  onPostEngagement?: (post: PublicPost) => void;
  post: PublicPost;
}) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const reactionState = usePostReaction(post.id, user?.uid);
  const queuedReaction = useQueuedPostReaction({
    postId: post.id,
    uid: user?.uid,
  });
  const setReaction = useSetPostReaction(user?.uid);
  const [optimisticReaction, setOptimisticReaction] =
    useState<OptimisticReactionState | null>(null);
  const [optimisticShareCount, setOptimisticShareCount] = useState<
    number | null
  >(null);
  const [shareMenuVisible, setShareMenuVisible] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [sharingAction, setSharingAction] = useState<SharePostAction | null>(
    null,
  );
  const [reshareComposerVisible, setReshareComposerVisible] = useState(false);
  const [reshareBody, setReshareBody] = useState('');
  const [reshareMentions, setReshareMentions] = useState<ProfileMention[]>([]);
  const sharingRef = useRef(false);
  const baseReactionCounts = post.reactionCounts ?? emptyReactionCounts();
  const optimisticCounts = optimisticReaction
    ? applyReactionTransition(optimisticReaction)
    : null;
  const queuedCounts =
    queuedReaction !== undefined && !optimisticCounts
      ? applyReactionTransition({
          baseCounts: baseReactionCounts,
          baseReaction: reactionState.reaction,
          reaction: queuedReaction,
        })
      : null;
  const visibleReaction = optimisticReaction
    ? optimisticReaction.reaction
    : queuedReaction !== undefined
      ? queuedReaction
      : reactionState.reaction;
  const visibleReactionCounts =
    optimisticCounts?.counts ?? queuedCounts?.counts ?? baseReactionCounts;
  const totalVisibleReactions = Object.values(visibleReactionCounts).reduce(
    (total, count) => total + count,
    0,
  );
  const visibleShareCount = optimisticShareCount ?? post.shareCount;
  const canOpenShareMenu =
    post.visibility === 'public' && Boolean(user?.uid) && !sharing;
  const canResharePost = post.visibility === 'public' && Boolean(user?.uid);
  const shareUrl = `https://frienzzi.com/posts/${post.id}`;
  const authorFullName = post.authorFullName || 'Frienzzi user';

  useEffect(() => {
    setOptimisticReaction(null);
    setOptimisticShareCount(null);
    sharingRef.current = false;
    setSharing(false);
    setSharingAction(null);
    setShareMenuVisible(false);
    setReshareComposerVisible(false);
    setReshareBody('');
    setReshareMentions([]);
  }, [post.id, user?.uid]);

  useEffect(() => {
    if (reactionState.error) {
      showError(reactionState.error, 'Unable to load your post reaction.');
    }
  }, [reactionState.error, showError]);

  useEffect(() => {
    if (
      optimisticReaction &&
      optimisticCounts &&
      shouldClearOptimisticReaction({
        currentCounts: baseReactionCounts,
        currentMutationAck: reactionState.mutationId,
        expectedCounts: optimisticCounts.counts,
        startingCounts: optimisticReaction.baseCounts,
        persistedReaction: reactionState.reaction,
        queuedReaction,
        targetMutationId: optimisticReaction.mutationId,
        targetReaction: optimisticReaction.reaction,
      })
    ) {
      setOptimisticReaction(null);
    }
  }, [
    baseReactionCounts,
    optimisticCounts,
    optimisticReaction,
    queuedReaction,
    reactionState.mutationId,
    reactionState.reaction,
  ]);

  useEffect(() => {
    if (
      optimisticShareCount != null &&
      post.shareCount >= optimisticShareCount
    ) {
      setOptimisticShareCount(null);
    }
  }, [optimisticShareCount, post.shareCount]);

  function chooseReaction(reaction: Reaction) {
    if (!user?.uid || reactionState.loading) {
      if (!user?.uid) {
        router.push('/sign-in');
      }
      return;
    }

    const nextReaction = visibleReaction === reaction ? null : reaction;
    const mutationId = createReactionMutationId();

    setOptimisticReaction({
      baseCounts:
        optimisticCounts?.counts ?? queuedCounts?.counts ?? baseReactionCounts,
      baseReaction: visibleReaction,
      mutationId,
      reaction: nextReaction,
    });

    setReaction.mutate(
      { mutationId, post, reaction: nextReaction },
      {
        onError: (error) => {
          setOptimisticReaction(null);
          showError(error, 'Unable to update that post reaction.');
        },
        onSuccess: () => {
          onPostEngagement?.(post);
        },
      },
    );
  }

  function openShareMenu() {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    if (!canOpenShareMenu) {
      return;
    }

    setShareMenuVisible(true);
  }

  function beginShareAction(action: SharePostAction) {
    if (!user?.uid || sharingRef.current || post.visibility !== 'public') {
      if (!user?.uid) {
        router.push('/sign-in');
      }
      return false;
    }

    sharingRef.current = true;
    setSharing(true);
    setSharingAction(action);
    trackEvent('post_share_start', { method: action, source: 'video_viewer' });
    return true;
  }

  function finishShareAction() {
    sharingRef.current = false;
    setSharing(false);
    setSharingAction(null);
  }

  async function recordSuccessfulShare(method: SharePostMethod) {
    try {
      const result = await recordPostShare(post.id);

      setOptimisticShareCount(result.shareCount);
      onPostEngagement?.(post);
      trackEvent('post_share_complete', { method, source: 'video_viewer' });
    } catch {
      trackEvent('post_share_count_failed', {
        method,
        source: 'video_viewer',
      });
    }
  }

  async function copyPostLink() {
    if (!beginShareAction('copy')) {
      return;
    }

    try {
      await copyTextToClipboard(shareUrl);
      await recordSuccessfulShare('clipboard');
      showSuccess('Post link copied.', 'Ready to share');
      setShareMenuVisible(false);
    } catch (error) {
      showError(error, 'Unable to copy that post link.');
    } finally {
      finishShareAction();
    }
  }

  async function sharePostWithDevice() {
    if (!beginShareAction('native')) {
      return;
    }

    try {
      const result = await shareLinkWithDevice({
        title: `${authorFullName} on Frienzzi`,
        text: post.body ?? 'View this video on Frienzzi.',
        url: shareUrl,
      });

      if (result.cancelled) {
        return;
      }

      await recordSuccessfulShare(result.method);
      if (result.method === 'clipboard') {
        showSuccess('Post link copied.', 'Ready to share');
      }
      setShareMenuVisible(false);
    } catch (error) {
      showError(error, 'Unable to share that post.');
    } finally {
      finishShareAction();
    }
  }

  async function sharePostToFacebook() {
    if (!beginShareAction('facebook')) {
      return;
    }

    try {
      await openFacebookShareDialog(shareUrl);
      await recordSuccessfulShare('facebook');
      showSuccess('Facebook share is ready.', 'Share opened');
      setShareMenuVisible(false);
    } catch (error) {
      showError(error, 'Unable to open Facebook sharing.');
    } finally {
      finishShareAction();
    }
  }

  function openReshareComposer() {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    if (!canResharePost) {
      return;
    }

    setShareMenuVisible(false);
    setReshareBody('');
    setReshareMentions([]);
    setReshareComposerVisible(true);
  }

  async function publishResharePostOnFrienzzi() {
    if (!beginShareAction('reshare')) {
      return;
    }

    const currentUser = user;
    const trimmedBody = reshareBody.trim();
    const activeMentions = visibleProfileMentions(reshareMentions, trimmedBody);

    try {
      if (!currentUser?.uid) {
        throw new Error('Sign in is required.');
      }

      await createPost({
        authorFullName: currentUser.displayName?.trim().slice(0, 80) || null,
        authorUid: currentUser.uid,
        body: trimmedBody,
        profileMentions: activeMentions,
        reshareOfPost: post,
        visibility: 'public',
      });
      await recordSuccessfulShare('reshare');
      trackEvent('post_reshare_create', { source: 'video_viewer' });
      showSuccess('Your reshare is live.', 'Reshared on Frienzzi');
      setReshareComposerVisible(false);
      setReshareBody('');
      setReshareMentions([]);
    } catch (error) {
      showError(error, 'Unable to reshare that post.');
    } finally {
      finishShareAction();
    }
  }

  return (
    <>
      <VideoActions
        currentUid={user?.uid}
        onActorProfilePress={onActorProfilePress}
        post={post}
        reactionCounts={visibleReactionCounts}
        reactionDisabled={!user?.uid || reactionState.loading}
        reactionLoading={false}
        selectedReaction={visibleReaction}
        shareCount={visibleShareCount}
        totalReactions={totalVisibleReactions}
        onCommentPress={() => {
          if (!user?.uid) {
            router.push('/sign-in');
            return;
          }

          onCommentPress(post);
        }}
        onOpenShare={openShareMenu}
        onReaction={chooseReaction}
      />
      <ShareOptionsModal
        busy={sharing}
        options={[
          ...(canResharePost
            ? [
                {
                  description:
                    'Create a new public post with the original embedded.',
                  kind: 'reshare' as const,
                  loading: sharingAction === 'reshare',
                  onPress: openReshareComposer,
                  title: 'Reshare on Frienzzi',
                },
              ]
            : []),
          {
            description: "Open Facebook with Frienzzi's share dialog.",
            kind: 'facebook',
            loading: sharingAction === 'facebook',
            onPress: () => {
              void sharePostToFacebook();
            },
            title: 'Share to Facebook',
          },
          {
            description: nativeShareDescription(),
            kind: 'native',
            loading: sharingAction === 'native',
            onPress: () => {
              void sharePostWithDevice();
            },
            title: nativeShareTitle(),
          },
          {
            description: 'Copy the post link so you can paste it anywhere.',
            kind: 'copy',
            loading: sharingAction === 'copy',
            onPress: () => {
              void copyPostLink();
            },
            title: 'Copy link',
          },
        ]}
        subtitle="Choose where this public Frienzzi video goes."
        title="Share video"
        visible={shareMenuVisible}
        onClose={() => {
          if (!sharing) {
            setShareMenuVisible(false);
          }
        }}
      />
      <ReshareVideoComposeModal
        body={reshareBody}
        currentUid={user?.uid}
        mentions={reshareMentions}
        post={post}
        posting={sharingAction === 'reshare'}
        visible={reshareComposerVisible}
        onBodyChange={setReshareBody}
        onClose={() => {
          if (!sharing) {
            setReshareComposerVisible(false);
          }
        }}
        onMentionsChange={setReshareMentions}
        onSubmit={() => {
          void publishResharePostOnFrienzzi();
        }}
      />
    </>
  );
}

export function PostVideoViewerModal({
  hasMorePosts = false,
  initialPost,
  initialPostId,
  loadingMorePosts = false,
  onClose,
  onCommentPress,
  onLoadMorePosts,
  onPostEngagement,
  posts,
  visible,
}: {
  hasMorePosts?: boolean;
  initialPost?: PublicPost | null;
  initialPostId: string | null;
  loadingMorePosts?: boolean;
  onClose: () => void;
  onCommentPress: (post: PublicPost) => void;
  onLoadMorePosts?: () => void;
  onPostEngagement?: (post: PublicPost) => void;
  posts: PublicPost[];
  visible: boolean;
}) {
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const { height, width } = useWindowDimensions();
  const incomingVideoPosts = useMemo(
    () => videoPostsFromPosts(posts, initialPost),
    [initialPost, posts],
  );
  const [videoPosts, setVideoPosts] =
    useState<PublicPost[]>(incomingVideoPosts);
  const initialIndex = videoPostIndexForPostId(
    incomingVideoPosts,
    initialPostId,
  );
  const listMountKey = initialPostId ?? 'post-video-viewer';
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [muted, setMuted] = useState(false);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [createWebPortal, setCreateWebPortal] = useState<
    | ((
        children: ReactNode,
        container: Element | DocumentFragment,
      ) => ReactNode)
    | null
  >(null);
  const [progressByPostId, setProgressByPostId] = useState<
    Record<string, VideoProgressState>
  >({});
  const listRef = useRef<FlatList<PublicPost>>(null);
  const activeIndexRef = useRef(initialIndex);
  const activePostIdRef = useRef<string | null>(null);
  const openingScrollTargetOffsetRef = useRef<number | null>(null);
  const pendingScrollActiveIndexRef = useRef<number | null>(null);
  const resetOpenKeyRef = useRef<string | null>(null);
  const scrollOffsetYRef = useRef(0);
  const trackedOpenKeyRef = useRef<string | null>(null);
  const seekersRef = useRef<
    Record<string, ((seconds: number) => void) | undefined>
  >({});
  const videoPostsRef = useRef(videoPosts);
  const activePost = videoPosts[activeIndex] ?? null;
  const activeProgress = activePost
    ? (progressByPostId[activePost.id] ?? EMPTY_VIDEO_PROGRESS)
    : EMPTY_VIDEO_PROGRESS;

  const handleProgressChange = useCallback(
    (postId: string, progress: VideoProgressState) => {
      setProgressByPostId((currentProgress) => ({
        ...currentProgress,
        [postId]: progress,
      }));
    },
    [],
  );

  const registerSeek = useCallback(
    (postId: string, seeker: ((seconds: number) => void) | null) => {
      if (seeker) {
        seekersRef.current[postId] = seeker;
        return;
      }

      delete seekersRef.current[postId];
    },
    [],
  );

  const seekActiveVideo = useCallback(
    (seconds: number) => {
      if (!activePost) {
        return;
      }

      seekersRef.current[activePost.id]?.(seconds);
    },
    [activePost],
  );
  const toggleActivePlayback = useCallback(() => {
    setPlaying((currentPlaying) => {
      const nextPlaying = !currentPlaying;

      trackEvent('post_video_viewer_play_toggle', {
        playing: nextPlaying,
      });

      return nextPlaying;
    });
  }, []);
  const minimizeViewer = useCallback(() => {
    trackEvent('post_video_viewer_minimize');
    setMinimized(true);
    setPlaying(false);
  }, []);
  const updateActiveVideoFromScrollOffset = useCallback(
    (offsetY: number, options?: { requireSettledOffset?: boolean }) => {
      if (height <= 0 || videoPostsRef.current.length === 0) {
        return;
      }

      scrollOffsetYRef.current = offsetY;

      const openingScrollTargetOffset = openingScrollTargetOffsetRef.current;

      if (openingScrollTargetOffset !== null) {
        const settledTolerance = Math.max(2, height * 0.02);

        if (Math.abs(offsetY - openingScrollTargetOffset) > settledTolerance) {
          return;
        }

        openingScrollTargetOffsetRef.current = null;
      }

      const nextIndex = videoViewerIndexForScrollOffset({
        height,
        offsetY,
        queueLength: videoPostsRef.current.length,
        requireSettledOffset: options?.requireSettledOffset,
      });

      if (nextIndex === null) {
        return;
      }

      if (nextIndex !== activeIndexRef.current) {
        setPlaying(true);
      }

      activeIndexRef.current = nextIndex;
      activePostIdRef.current = videoPostsRef.current[nextIndex]?.id ?? null;
      setActiveIndex(nextIndex);
      setCaptionExpanded(false);
      trackEvent('post_video_viewer_swipe', {
        position: nextIndex,
      });
    },
    [height],
  );

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return undefined;
    }

    let active = true;

    void import('react-dom').then(({ createPortal }) => {
      if (active) {
        setCreateWebPortal(() => createPortal);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    videoPostsRef.current = videoPosts;
  }, [videoPosts]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setVideoPosts((currentQueue) => {
      const mergedQueue = mergeVideoViewerQueue(
        currentQueue,
        posts,
        initialPost,
      );
      const activePostId = activePostIdRef.current;
      const activePostNextIndex = activePostId
        ? mergedQueue.findIndex((post) => post.id === activePostId)
        : -1;
      const nextIndex = videoViewerIndexForScrollOffset({
        height,
        offsetY: scrollOffsetYRef.current,
        queueLength: mergedQueue.length,
        requireSettledOffset: true,
      });

      if (
        activePostNextIndex >= 0 &&
        activePostNextIndex !== activeIndexRef.current
      ) {
        pendingScrollActiveIndexRef.current = activePostNextIndex;
      } else if (
        nextIndex !== null &&
        mergedQueue.length > currentQueue.length &&
        nextIndex >= currentQueue.length
      ) {
        pendingScrollActiveIndexRef.current = nextIndex;
      } else if (
        mergedQueue.length > 0 &&
        !mergedQueue.some((post) => post.id === activePostIdRef.current)
      ) {
        pendingScrollActiveIndexRef.current = nextIndex ?? 0;
      }

      videoPostsRef.current = mergedQueue;

      return mergedQueue;
    });
  }, [height, initialPost, posts, visible]);

  useEffect(() => {
    if (!visible) {
      pendingScrollActiveIndexRef.current = null;
      return;
    }

    const pendingIndex = pendingScrollActiveIndexRef.current;

    if (pendingIndex === null || !videoPosts[pendingIndex]) {
      return;
    }

    pendingScrollActiveIndexRef.current = null;

    if (pendingIndex !== activeIndexRef.current) {
      setPlaying(true);
    }

    activeIndexRef.current = pendingIndex;
    activePostIdRef.current = videoPosts[pendingIndex]?.id ?? null;
    setActiveIndex(pendingIndex);
    setCaptionExpanded(false);
    requestAnimationFrame(() => {
      listRef.current?.scrollToIndex({
        animated: false,
        index: pendingIndex,
      });
    });
  }, [videoPosts, visible]);

  useEffect(() => {
    if (!visible || !activePost) {
      trackedOpenKeyRef.current = null;
      return;
    }

    const openKey = `${activePost.id}:${initialPostId ?? ''}`;

    if (trackedOpenKeyRef.current === openKey) {
      return;
    }

    trackedOpenKeyRef.current = openKey;
    trackEvent('post_video_viewer_open', {
      initial_position: activeIndex,
      queue_size: videoPosts.length,
    });
  }, [activeIndex, activePost, initialPostId, videoPosts.length, visible]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
    activePostIdRef.current = videoPosts[activeIndex]?.id ?? null;
  }, [activeIndex, videoPosts]);

  useEffect(() => {
    if (
      !visible ||
      !hasMorePosts ||
      loadingMorePosts ||
      !onLoadMorePosts ||
      videoPosts.length === 0
    ) {
      return;
    }

    if (activeIndex >= Math.max(0, videoPosts.length - 2)) {
      onLoadMorePosts();
    }
  }, [
    activeIndex,
    hasMorePosts,
    loadingMorePosts,
    onLoadMorePosts,
    videoPosts.length,
    visible,
  ]);

  useLayoutEffect(() => {
    if (!visible) {
      resetOpenKeyRef.current = null;
      activePostIdRef.current = null;
      openingScrollTargetOffsetRef.current = null;
      setCaptionExpanded(false);
      setMinimized(false);
      setPlaying(true);
      return;
    }

    const openKey = initialPostId ?? 'post-video-viewer';

    if (resetOpenKeyRef.current === openKey) {
      return;
    }

    const openVideoPosts = videoPostsFromPosts(posts, initialPost);
    const openInitialIndex = videoPostIndexForPostId(
      openVideoPosts,
      initialPostId,
    );

    resetOpenKeyRef.current = openKey;
    scrollOffsetYRef.current = height * openInitialIndex;
    openingScrollTargetOffsetRef.current = height * openInitialIndex;
    videoPostsRef.current = openVideoPosts;
    activeIndexRef.current = openInitialIndex;
    activePostIdRef.current = openVideoPosts[openInitialIndex]?.id ?? null;
    setVideoPosts(openVideoPosts);
    setActiveIndex(openInitialIndex);
    setCaptionExpanded(false);
    setMinimized(false);
    setPlaying(true);

    requestAnimationFrame(() => {
      listRef.current?.scrollToIndex({
        animated: false,
        index: openInitialIndex,
      });
    });
  }, [height, initialPost, initialPostId, posts, visible]);

  function openAuthorProfile(post: PublicPost) {
    if (!user?.uid) {
      onClose();
      router.push('/sign-in');
      return;
    }

    trackEvent('profile_open', {
      source: 'post_video_viewer',
      target_is_current_user: post.authorUid === user.uid,
    });
    setMinimized(true);
    setPlaying(false);
    requestAnimationFrame(() => {
      openUserProfile({ currentUid: user.uid, targetUid: post.authorUid });
    });
  }

  function openReactionActorProfile(targetUid: string) {
    if (!user?.uid) {
      onClose();
      router.push('/sign-in');
      return;
    }

    trackEvent('profile_open', {
      source: 'post_video_reaction_details',
      target_is_current_user: targetUid === user.uid,
    });
    setMinimized(true);
    setPlaying(false);
    requestAnimationFrame(() => {
      openUserProfile({ currentUid: user.uid, targetUid });
    });
  }

  if (!visible || !activePost || videoPosts.length === 0) {
    return null;
  }

  if (minimized) {
    const miniPlayerOverlay = (
      <View pointerEvents="box-none" style={styles.minimizedRoot}>
        <Pressable
          accessibilityLabel="Return to video"
          accessibilityRole="button"
          onPress={() => {
            setMinimized(false);
            setPlaying(true);
          }}
          style={({ pressed }) => [
            styles.miniPlayer,
            pressed ? styles.miniPlayerPressed : null,
          ]}
        >
          {activePost.media?.thumbnail?.downloadURL ? (
            <ExpoImage
              accessibilityLabel=""
              cachePolicy="memory-disk"
              contentFit="cover"
              source={{ uri: activePost.media.thumbnail.downloadURL }}
              style={styles.miniPlayerImage}
            />
          ) : (
            <View style={styles.miniPlayerFallback}>
              <Play color={colors.surface} fill={colors.surface} size={24} />
            </View>
          )}
          <Pressable
            accessibilityLabel="Close minimized video"
            accessibilityRole="button"
            hitSlop={8}
            onPress={(event) => {
              event.stopPropagation();
              onClose();
            }}
            style={({ pressed }) => [
              styles.miniPlayerClose,
              pressed ? styles.miniPlayerClosePressed : null,
            ]}
          >
            <X color={colors.surface} size={14} strokeWidth={2.8} />
          </Pressable>
          <View style={styles.miniPlayerOverlay}>
            <Play color={colors.surface} fill={colors.surface} size={18} />
            <Text numberOfLines={1} style={styles.miniPlayerText}>
              Back to video
            </Text>
          </View>
        </Pressable>
      </View>
    );

    if (
      Platform.OS === 'web' &&
      typeof document !== 'undefined' &&
      createWebPortal
    ) {
      return <>{createWebPortal(miniPlayerOverlay, document.body)}</>;
    }

    return (
      <Modal transparent visible onRequestClose={onClose}>
        {miniPlayerOverlay}
      </Modal>
    );
  }

  return (
    <Modal
      animationType="fade"
      presentationStyle="fullScreen"
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.viewerRoot}>
        <FlatList
          key={listMountKey}
          ref={listRef}
          data={videoPosts}
          decelerationRate="fast"
          getItemLayout={(_, index) => ({
            index,
            length: height,
            offset: height * index,
          })}
          initialScrollIndex={initialIndex}
          keyExtractor={(post) => post.id}
          onScrollToIndexFailed={(info) => {
            requestAnimationFrame(() => {
              listRef.current?.scrollToOffset({
                animated: false,
                offset: info.averageItemLength * info.index,
              });
            });
          }}
          onEndReached={() => {
            if (hasMorePosts && !loadingMorePosts) {
              onLoadMorePosts?.();
            }
          }}
          onEndReachedThreshold={0.75}
          onScroll={(event) => {
            updateActiveVideoFromScrollOffset(
              event.nativeEvent.contentOffset.y,
              { requireSettledOffset: true },
            );
          }}
          onMomentumScrollEnd={(event) => {
            updateActiveVideoFromScrollOffset(
              event.nativeEvent.contentOffset.y,
            );
          }}
          onScrollEndDrag={(event) => {
            updateActiveVideoFromScrollOffset(
              event.nativeEvent.contentOffset.y,
              { requireSettledOffset: true },
            );
          }}
          pagingEnabled
          renderItem={({ index, item }) => (
            <VideoSlide
              active={item.id === activePost.id && visible && playing}
              height={height}
              muted={muted}
              onProgressChange={handleProgressChange}
              onRegisterSeek={registerSeek}
              post={item}
              width={width}
              onTogglePlayback={setPlaying}
            />
          )}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
          snapToAlignment="start"
          snapToInterval={height}
          ListFooterComponent={
            loadingMorePosts ? (
              <View style={[styles.loadingMoreSlide, { height }]}>
                <ActivityIndicator color={colors.surface} />
                <Text style={styles.loadingMoreText}>Loading more videos</Text>
              </View>
            ) : null
          }
        />
        <View
          pointerEvents="box-none"
          style={[
            styles.safeOverlay,
            {
              bottom: Math.max(insets.bottom, spacing.md),
              left: Math.max(insets.left, 0),
              right: Math.max(insets.right, 0),
              top: Math.max(insets.top, spacing.md),
            },
          ]}
        >
          <VideoOverlay
            captionExpanded={captionExpanded}
            currentUid={user?.uid}
            muted={muted}
            post={activePost}
            playing={playing}
            progress={activeProgress}
            onClose={onClose}
            onMinimize={minimizeViewer}
            onOpenAuthor={() => openAuthorProfile(activePost)}
            onSeek={seekActiveVideo}
            onTogglePlayback={toggleActivePlayback}
            onToggleCaption={() => setCaptionExpanded((current) => !current)}
            onToggleMuted={() => setMuted((current) => !current)}
          />
          <ActiveVideoControls
            onActorProfilePress={openReactionActorProfile}
            post={activePost}
            onCommentPress={onCommentPress}
            onPostEngagement={onPostEngagement}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  actionCluster: {
    alignItems: 'center',
    gap: 2,
  },
  actionCount: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '900',
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 5,
  },
  actionsRail: {
    alignItems: 'center',
    bottom: 206,
    gap: spacing.sm,
    position: 'absolute',
    right: spacing.md,
    width: 50,
  },
  authorRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  viewerIdentity: {
    flex: 1,
  },
  authorTextBlock: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  viewerAuthorRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
    minWidth: 0,
  },
  bottomOverlay: {
    backgroundColor: 'rgba(0,0,0,0.32)',
    borderColor: 'rgba(255,255,255,0.1)',
    borderRadius: 16,
    borderWidth: 1,
    gap: spacing.sm,
    marginHorizontal: spacing.md,
    maxWidth: 560,
    padding: spacing.sm,
    paddingRight: 86,
  },
  bottomContent: {
    bottom: 0,
    gap: spacing.sm,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  captionPressable: {
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  captionScroll: {
    maxHeight: 44,
  },
  captionScrollExpanded: {
    maxHeight: 168,
  },
  followButton: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    borderRadius: 999,
    borderWidth: 1,
    justifyContent: 'center',
    minHeight: 32,
    minWidth: 84,
    paddingHorizontal: spacing.sm,
  },
  followButtonDisabled: {
    opacity: 0.64,
  },
  followButtonFollowing: {
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderColor: 'rgba(255,255,255,0.45)',
  },
  followButtonPressed: {
    transform: [{ scale: 0.97 }],
  },
  followButtonText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '900',
  },
  followButtonTextFollowing: {
    color: colors.surface,
  },
  loadingMoreSlide: {
    alignItems: 'center',
    backgroundColor: '#000000',
    gap: spacing.sm,
    justifyContent: 'center',
  },
  loadingMoreText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '800',
    opacity: 0.76,
  },
  minimizedRoot: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  miniPlayer: {
    backgroundColor: '#000000',
    borderColor: 'rgba(255,255,255,0.32)',
    borderRadius: 14,
    borderWidth: 1,
    bottom: spacing.lg,
    height: 112,
    left: spacing.lg,
    overflow: 'hidden',
    position: 'absolute',
    shadowColor: '#000000',
    shadowOffset: { height: 10, width: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 18,
    width: 150,
  },
  miniPlayerFallback: {
    alignItems: 'center',
    backgroundColor: '#000000',
    flex: 1,
    justifyContent: 'center',
  },
  miniPlayerClose: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.62)',
    borderColor: 'rgba(255,255,255,0.28)',
    borderRadius: 999,
    borderWidth: 1,
    height: 24,
    justifyContent: 'center',
    position: 'absolute',
    right: 6,
    top: 6,
    width: 24,
  },
  miniPlayerClosePressed: {
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  miniPlayerImage: {
    height: '100%',
    width: '100%',
  },
  miniPlayerOverlay: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.46)',
    bottom: 0,
    flexDirection: 'row',
    gap: spacing.xs,
    left: 0,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    position: 'absolute',
    right: 0,
  },
  miniPlayerPressed: {
    transform: [{ scale: 0.98 }],
  },
  miniPlayerText: {
    color: colors.surface,
    flex: 1,
    fontSize: 12,
    fontWeight: '900',
  },
  progressBuffered: {
    backgroundColor: 'rgba(255,255,255,0.24)',
    borderRadius: 999,
    bottom: 0,
    left: 0,
    position: 'absolute',
    top: 0,
  },
  progressControl: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.34)',
    borderTopColor: 'rgba(255,255,255,0.12)',
    borderTopWidth: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  progressFill: {
    backgroundColor: colors.primary,
    borderRadius: 999,
    bottom: 0,
    left: 0,
    position: 'absolute',
    top: 0,
  },
  progressMain: {
    flex: 1,
    gap: 5,
    minWidth: 0,
  },
  progressPlayButton: {
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderColor: 'rgba(255,255,255,0.22)',
    borderRadius: 999,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    width: 34,
  },
  progressPlayButtonPressed: {
    backgroundColor: 'rgba(255,255,255,0.24)',
    transform: [{ scale: 0.96 }],
  },
  progressThumb: {
    backgroundColor: colors.surface,
    borderColor: colors.primary,
    borderRadius: 999,
    borderWidth: 2,
    height: 14,
    marginLeft: -7,
    marginTop: -5,
    position: 'absolute',
    top: '50%',
    width: 14,
  },
  progressTimeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  progressTimeText: {
    color: colors.surface,
    fontSize: 11,
    fontWeight: '800',
    opacity: 0.86,
  },
  progressTrack: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 999,
    height: 4,
    overflow: 'visible',
    position: 'relative',
  },
  centerPlayIndicator: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.34)',
    borderRadius: 40,
    height: 72,
    justifyContent: 'center',
    left: '50%',
    marginLeft: -36,
    marginTop: -36,
    position: 'absolute',
    top: '50%',
    width: 72,
  },
  composeActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  composeBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 12, 62, 0.38)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  composeButton: {
    flex: 1,
  },
  composeCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    gap: spacing.lg,
    maxHeight: '92%',
    maxWidth: 520,
    padding: spacing.lg,
    shadowColor: '#000000',
    shadowOffset: { height: 10, width: 0 },
    shadowOpacity: 0.16,
    shadowRadius: 24,
    width: '100%',
  },
  composeCloseButton: {
    alignItems: 'center',
    borderRadius: 18,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  composeCounter: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
    marginTop: -spacing.sm,
    textAlign: 'right',
  },
  composeHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  composeInput: {
    minHeight: 94,
  },
  composeScroll: {
    flexGrow: 0,
  },
  composeScrollContent: {
    gap: spacing.md,
  },
  composeSubtitle: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
  },
  composeTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  composeTitleBlock: {
    flex: 1,
    gap: 3,
  },
  moreText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
    marginTop: 2,
    opacity: 0.9,
  },
  overlayButton: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.38)',
    borderRadius: 22,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  overlayButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  overlayButtonPressedDark: {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  pauseBadge: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.42)',
    borderRadius: 18,
    height: 36,
    justifyContent: 'center',
    left: spacing.md,
    position: 'absolute',
    top: 54,
    width: 36,
  },
  railButton: {
    alignItems: 'center',
    borderRadius: 999,
    gap: 2,
    justifyContent: 'center',
    paddingVertical: 2,
    width: 50,
  },
  railButtonPressed: {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  railIconCircle: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.32)',
    borderColor: 'rgba(255,255,255,0.16)',
    borderRadius: 999,
    borderWidth: 1,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  resharePreview: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xs,
    padding: spacing.md,
  },
  resharePreviewBody: {
    color: colors.secondary,
    fontSize: 13,
    lineHeight: 19,
  },
  resharePreviewEyebrow: {
    color: colors.primary,
    fontSize: 11,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  resharePreviewTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  safeOverlay: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  slide: {
    alignItems: 'center',
    backgroundColor: '#000000',
    justifyContent: 'center',
  },
  topOverlay: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    left: spacing.md,
    position: 'absolute',
    right: spacing.md,
    top: 0,
  },
  topLeftActions: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  topReactionButton: {
    alignItems: 'center',
    borderRadius: 24,
    justifyContent: 'center',
    minHeight: 34,
    paddingVertical: 2,
    width: 50,
  },
  topReactionBubble: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderColor: 'rgba(255,255,255,0.18)',
    borderRadius: 999,
    borderWidth: 1,
    height: 26,
    justifyContent: 'center',
    width: 26,
  },
  topReactionBubbleOverlap: {
    marginLeft: -8,
  },
  topReactionEmoji: {
    fontSize: 18,
    lineHeight: 22,
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 4,
  },
  topReactionStack: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    minWidth: 44,
  },
  video: {
    height: '100%',
    width: '100%',
  },
  videoFallback: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  videoFallbackText: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: '900',
  },
  videoLoadingOverlay: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.4)',
    bottom: 0,
    gap: spacing.sm,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  videoLoadingText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 4,
  },
  viewerAuthor: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: '900',
    textShadowColor: 'rgba(0,0,0,0.55)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 5,
  },
  viewerAvatar: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.surface,
    borderRadius: 22,
    borderWidth: 2,
    height: 44,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 44,
  },
  viewerAvatarImage: {
    height: '100%',
    width: '100%',
  },
  viewerAvatarText: {
    color: colors.secondary,
    fontSize: 17,
    fontWeight: '900',
  },
  viewerCaption: {
    color: colors.surface,
    fontSize: 14,
    lineHeight: 20,
    textShadowColor: 'rgba(0,0,0,0.55)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 5,
  },
  viewerCaptionMention: {
    color: colors.primary,
    fontWeight: '900',
  },
  viewerMeta: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 12,
    fontWeight: '800',
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 4,
  },
  viewerRoot: {
    backgroundColor: '#000000',
    flex: 1,
  },
});
